import { useMemo } from 'react';
import {
  BordBadge,
  BordCard,
  BordOneToneIcon,
  type CardCollapsedRightContentConfig,
  type CardLeftContentProps,
  type CardLeftFeatureTag
} from 'bord-design-system';
import { bordColorTheme } from '../../styles/styles.constants';
import { IInTransitLocation } from 'types/products';

import './SelectableToolCard.scss';

export interface IToolData {
  id?: number;
  productId?: number | null;
  name?: string | null;
  pills?: { [key: string]: string | null };
  image?: string | null;
  brand?: string | null;
  brandImage?: string | null;
  serial?: string | null;
  isAdditionalProduct?: boolean | null;
  isMarkedNotDelivered?: boolean | null;
  inTransit?: IInTransitLocation | null;
  isUploaded?: boolean | null;
}

interface ISelectableToolCard {
  tool: IToolData;
  isSelected?: boolean;
  onSelectionChange?: () => void;
  showCheckbox?: boolean;
  customClassName?: string;
  isUnselected?: boolean;
}

/**
 * Fila de herramienta seleccionable basada en `BordCard` del design system.
 * @param tool - La herramienta a mostrar
 * @param isSelected - Indica si la herramienta está seleccionada
 * @param onSelectionChange - Función al alternar la selección (solo con `showCheckbox`)
 * @param showCheckbox - Muestra u oculta el checkbox de selección
 * @param customClassName - Clases en el contenedor externo (p. ej. bordes)
 */
const SelectableToolCard = ({
  tool,
  isSelected = false,
  onSelectionChange,
  showCheckbox = false,
  customClassName = '',
  isUnselected = false
}: ISelectableToolCard) => {
  const displayName = tool.name?.trim() ? tool.name : 'Sin nombre';
  const serialText = tool?.serial?.trim() ? tool.serial : 'Sin serial';
  const showAdditionalBadge = Boolean(tool.isAdditionalProduct);

  const featureTags = useMemo((): CardLeftFeatureTag[] => {
    const tags: CardLeftFeatureTag[] = [];
    if (tool.pills) {
      Object.entries(tool.pills).forEach(([key, value], index) => {
        if (value) {
          tags.push({ tagKey: `${key}-${index}`, labelName: value, standardSize: 'small' });
        }
      });
    }
    return tags;
  }, [tool.pills]);

  const renderTags = featureTags?.length > 0 ? featureTags?.slice(0, 3) : undefined

  const leftBlock: CardLeftContentProps = {
    title: displayName,
    titleClassName: 'customLeftBlockTitle',
    featureTags: renderTags,
    bordPhotoProps: {
      url: tool.image ?? undefined,
      size: 's56',
      variant: 'square',
      imageProps: {
        style: {
          height: '100%',
          objectFit: 'contain',
        },
      },
      style: {
         border: `1px solid ${bordColorTheme.white[400]}`
      },
      toolModelMarkerProps: {
        isBordTool: true,
        customSize: 3.2
      }
    }
  }

  const renderAdditionalRightContent = () => (
    <div className="selectableToolCardAdditionalRight">
      <BordBadge className='text-[10px]' label="Herramienta adicional" variant="gradient" />
      <div className="selectableToolCardSerialRow">
        <BordOneToneIcon variant="barCode2" customWidth="12px" stroke={bordColorTheme.stroke.default} />
        <span className="serialText">{serialText}</span>
      </div>
    </div>
  );

  const rightContent = showAdditionalBadge ? renderAdditionalRightContent() : undefined;

  const checkboxConfig = {
    show: showCheckbox,
    isActive: isSelected,
    isDisabled: false,
    onChange: onSelectionChange ? () => onSelectionChange() : undefined
  }

  const collapsedRightContent: CardCollapsedRightContentConfig = {
    text: serialText,
    textClassName: 'customCollapsedRightContentTitle',
    iconVariant: 'barCode2',
    iconProps: { customWidth: '12px', stroke: bordColorTheme.stroke.default },
  }

  return (
    <div className={`selectableToolCard ${customClassName}`}>
      <BordCard
        className={isUnselected ? 'bg-red-bright-100' : 'bg-white-75'}
        leftBlock={leftBlock}
        rightContent={rightContent}
        checkbox={checkboxConfig}
        collapsedRightContent={collapsedRightContent}
        showBottomContent={false}
        showUnitControls={false}
      />
    </div>
  );
};

export default SelectableToolCard;
